// LoginPage — WebLogin adapted from auth-screens.jsx.
// Wired to POST /auth/login. Google OAuth button is visual-only placeholder.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { extractApiError } from '@/lib/api';
import { Spinner, notYetWiredToast } from '@/components/primitives';

const IMG = (id: string, w = 1200) =>
  `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop`;

const T = {
  bg:      '#0A0F1C',
  bg2:     '#0F1628',
  surface: '#131C30',
  ink:     '#F4F3F0',
  inkDim:  '#8C98B0',
  primary: '#E8441A',
  accent:  '#4B8EDB',
  line:    'rgba(255,255,255,0.07)',
  lineHi:  'rgba(255,255,255,0.14)',
  serif:   '"Fraunces Variable","Fraunces",Georgia,serif',
  display: '"Inter Variable","Inter",system-ui,sans-serif',
};

function GoogleGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

function WebField({
  label, value, onChange, placeholder, type = 'text', hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  type?: string;
  hint?: string;
}) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontFamily: T.display, fontSize: 11, color: T.inkDim, letterSpacing: 1.5, fontWeight: 700, textTransform: 'uppercase' }}>{label}</span>
        {hint && <button style={{ background: 'none', border: 'none', color: T.primary, fontFamily: T.display, fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>{hint}</button>}
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        placeholder={placeholder}
        style={{
          width: '100%', height: 48, borderRadius: 12, padding: '0 16px',
          background: T.bg, color: T.ink,
          border: `1.5px solid ${focus ? T.primary : T.lineHi}`,
          fontFamily: T.display, fontSize: 15, fontWeight: 500, outline: 'none',
          transition: 'border-color .15s', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

export function LoginPage() {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      if (mode === 'signup') {
        notYetWiredToast('Account Registration');
        return;
      }
      await login(email, password);
      navigate('/');
    } catch (err) {
      const apiErr = extractApiError(err);
      toast.error(apiErr.message ?? 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50, background: T.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32,
    }}>
      <div style={{
        width: 940, maxWidth: '100%', height: 640, maxHeight: '92vh',
        background: T.surface, borderRadius: 28, overflow: 'hidden',
        display: 'flex', border: `1px solid ${T.lineHi}`,
        boxShadow: '0 40px 100px rgba(0,0,0,0.6)',
      }}>
        {/* LEFT — photo storytelling */}
        <div style={{
          flex: '0 0 42%', position: 'relative',
          background: `url(${IMG('1507525428034-b723cf961d3e')}) center/cover`,
        }}>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10,12,16,0.3) 0%, rgba(10,12,16,0.85) 100%)' }}/>
          <div style={{ position: 'absolute', inset: 0, padding: 36, display: 'flex', flexDirection: 'column', color: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg width="28" height="28" viewBox="0 0 32 32">
                <path d="M4 6l8 18 4-9 8 11" stroke="#E8441A" strokeWidth="2.6" strokeLinecap="round" fill="none" strokeLinejoin="round"/>
                <circle cx="26" cy="6" r="3" fill="#E8441A"/>
              </svg>
              <span style={{ fontFamily: T.display, fontSize: 19, fontWeight: 700, letterSpacing: -0.5 }}>global flyt<span style={{ color: '#E8441A' }}>.</span></span>
            </div>
            <div style={{ flex: 1 }}/>
            <div style={{ fontFamily: T.display, fontSize: 11, letterSpacing: 3, fontWeight: 600, color: '#E8441A' }}>● MEMBERS · GOLD UNLOCKED AT 3 TRIPS</div>
            <h2 style={{ fontFamily: T.display, fontSize: 56, lineHeight: 0.9, fontWeight: 600, margin: '12px 0 0', letterSpacing: -0.03 }}>
              Your trips,<br/>
              <span style={{ fontFamily: T.serif, fontStyle: 'italic', fontWeight: 400 }}>your way.</span>
            </h2>
            <div style={{ display: 'flex', gap: 14, marginTop: 24 }}>
              {[
                ['$240', 'avg saved\nper member'],
                ['9.6/10', 'satisfaction\nthis year'],
                ['24h', 'free cancel\non everything'],
              ].map(([v, l]) => (
                <div key={v} style={{ flex: 1 }}>
                  <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, letterSpacing: -0.02 }}>{v}</div>
                  <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 11, opacity: 0.75, lineHeight: 1.4, marginTop: 2, whiteSpace: 'pre-line' }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — form */}
        <div style={{ flex: 1, padding: 44, display: 'flex', flexDirection: 'column', position: 'relative', overflowY: 'auto' }}>
          <button onClick={() => navigate('/')} style={{
            position: 'absolute', top: 18, right: 18, width: 36, height: 36, borderRadius: 10,
            background: T.bg, border: `1px solid ${T.line}`, cursor: 'pointer',
            color: T.ink, fontSize: 18,
          }}>×</button>

          {/* Tabs */}
          <div style={{ display: 'flex', padding: 4, borderRadius: 14, background: T.bg, border: `1px solid ${T.line}`, marginBottom: 28 }}>
            {[['signin', 'Sign in'], ['signup', 'Create account']].map(([k, l]) => (
              <button key={k} onClick={() => setMode(k as 'signin' | 'signup')} style={{
                flex: 1, height: 38, borderRadius: 10, border: 'none', cursor: 'pointer',
                background: mode === k ? T.surface : 'transparent',
                color: mode === k ? T.ink : T.inkDim,
                fontFamily: T.display, fontSize: 13, fontWeight: 600,
              }}>{l}</button>
            ))}
          </div>

          <div>
            <h2 style={{ fontFamily: T.display, fontSize: 36, fontWeight: 600, color: T.ink, margin: 0, letterSpacing: -0.02, lineHeight: 1.05 }}>
              {mode === 'signin' ? 'Welcome back.' : 'Make it official.'}
            </h2>
            <p style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 14, color: T.inkDim, margin: '8px 0 0' }}>
              {mode === 'signin' ? 'Sign in to pick up where you left off.' : "It takes 30 seconds. We'll never spam."}
            </p>

            {mode === 'signup' && (
              <WebField label="Name" value={name} onChange={setName} placeholder="Your name"/>
            )}
            <WebField label="Email" type="email" value={email} onChange={setEmail} placeholder="you@somewhere.com"/>
            <WebField
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="••••••••"
              {...(mode === 'signin' ? { hint: 'Forgot?' } : {})}
            />

            {mode === 'signup' && (
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 8, fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 12, color: T.inkDim, lineHeight: 1.5, cursor: 'pointer' }}>
                <input type="checkbox" defaultChecked style={{ accentColor: T.primary, width: 16, height: 16, marginTop: 2 }}/>
                <span>Send me deals, drops &amp; travel inspo. Unsubscribe anytime.</span>
              </label>
            )}

            <button
              onClick={() => void handleSubmit()}
              disabled={loading}
              style={{
                width: '100%', height: 52, marginTop: 18, borderRadius: 14,
                background: T.primary, color: T.bg, border: 'none', cursor: loading ? 'wait' : 'pointer',
                fontFamily: T.display, fontSize: 15, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                opacity: loading ? 0.8 : 1,
              }}
            >
              {loading ? <Spinner/> : mode === 'signin' ? 'Sign in →' : 'Create my account →'}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0' }}>
              <div style={{ flex: 1, height: 1, background: T.line }}/>
              <span style={{ fontFamily: T.display, fontSize: 11, color: T.inkDim, letterSpacing: 1.5, fontWeight: 600 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: T.line }}/>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
              {[
                { icon: <GoogleGlyph/>, label: 'Google', action: () => notYetWiredToast('Google OAuth') },
                { icon: <span style={{ fontFamily: T.display, fontSize: 18, fontWeight: 700, color: T.ink }}>𝕏</span>, label: 'X', action: () => notYetWiredToast('X OAuth') },
                { icon: <span style={{ fontSize: 18 }}>📱</span>, label: 'Phone', action: () => notYetWiredToast('Phone OTP') },
              ].map((b, i) => (
                <button key={i} onClick={b.action} style={{
                  height: 48, borderRadius: 12,
                  background: T.bg, border: `1px solid ${T.line}`, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{b.icon}</button>
              ))}
            </div>
          </div>

          <div style={{ flex: 1 }}/>
          <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 11, color: T.inkDim, textAlign: 'center', lineHeight: 1.5, marginTop: 24 }}>
            By continuing you agree to our <span style={{ color: T.ink, fontWeight: 600 }}>Terms</span> and <span style={{ color: T.ink, fontWeight: 600 }}>Privacy Policy</span>.
          </div>
        </div>
      </div>
    </div>
  );
}
